// UI-triggered sign-out: run Clerk's supported client-side signOut() before
// navigating to /api/logout. Clerk's custom sign-out flow docs
// (clerk.com/docs/guides/development/custom-flows/authentication/sign-out)
// use signOut() from the Clerk class; it ends the session at Clerk's Frontend
// API and removes the session cookies clerk-js manages. /api/logout then
// clears the TWT app and remember-me cookies, revokes server-side, and
// expires the Clerk cookies again as a belt-and-braces server clear.
//
// Every failure path still reaches /api/logout: a broken or absent Clerk
// script must never trap a member in a signed-in browser.

import type { MouseEvent as ReactMouseEvent } from "react";

type ClerkGlobal = {
  loaded?: boolean;
  session?: unknown;
  signOut?: () => Promise<void>;
};

function clerkGlobal(): ClerkGlobal | null {
  if (typeof window === "undefined") return null;
  const clerk = (window as { Clerk?: ClerkGlobal }).Clerk;
  return clerk ?? null;
}

/** Clerk client sign-out, bounded and swallowed: resolves within ~2s always. */
export async function signOutOfClerkBrowser(): Promise<void> {
  const clerk = clerkGlobal();
  if (!clerk?.loaded || !clerk.session || !clerk.signOut) return;
  await Promise.race([
    clerk.signOut().catch(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, 2000)),
  ]);
}

/**
 * Click handler for the plain `<a href="/api/logout...">` sign-out links.
 * With no live Clerk session the default navigation proceeds untouched.
 * With one, the navigation waits for the client sign-out (or its timeout)
 * and then continues to the same validated href, so `?next=` still lands
 * where the link said. Modified clicks (new tab, middle click) are left to
 * the browser.
 */
export function clerkAwareLogoutClick(
  event: ReactMouseEvent<HTMLAnchorElement>
): void {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }
  const clerk = clerkGlobal();
  if (!clerk?.loaded || !clerk.session || !clerk.signOut) return;
  const href = event.currentTarget.href;
  event.preventDefault();
  void signOutOfClerkBrowser().finally(() => {
    window.location.assign(href);
  });
}
